import type { ConnectedAccountResponse } from '../contracts/ConnectedAccountResponse';
import { ErrorResponse, ErrorType } from '../contracts/ErrorResponse';
import { OAuthProviderType } from '../contracts/SharedProperties';
import loginToIdp, { LoginConfig } from '../identityProvider/loginToIdp';
import { addConnectedAccount } from '../services/addConnectedAccount';
import type { AddConnectedAccountHandler } from '../types/AddConnectedAccountHandler';
import type { ConnectedAccountRequest } from '../contracts/ConnectedAccountRequest';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { logUsage } from 'owa-analytics';
import isProviderHostAvailable from '../utils/isProviderHostAvailable';

const providerAddConnectedAccountHandler: {
    [providerType: string]: AddConnectedAccountHandler;
} = {};

function getAddConnectedAccountHandler(
    providerType: OAuthProviderType
): AddConnectedAccountHandler {
    return providerAddConnectedAccountHandler[providerType] || addConnectedAccount;
}

export function setAddConnectedAccountHandler(
    providerType: OAuthProviderType,
    addConnectedAccountHandler: AddConnectedAccountHandler
) {
    providerAddConnectedAccountHandler[providerType] = addConnectedAccountHandler;
}

const defaultRedirectUri = getOrigin() + '/mail/oauthRedirect.html';

//to do: get the config from the server
const idpLoginConfig: LoginConfig = {
    [OAuthProviderType.Msa]: {
        clientId: '292841',
        scope: 'service::outlook.office.com::MBI_SSL',
        display: 'popup',
        response_type: 'code',
        oauth_proxy: '',
        force: true,
        redirect_uri: defaultRedirectUri,
    },
    [OAuthProviderType.TeamSnap]: {
        clientId: '27ce19d16eee291f13ae63dbd1e02e345dea8ea3dbd2c9e8fb7b7340e07dfe43',
        scope: 'read',
        display: 'popup',
        oauth_proxy: '',
        redirect_uri: defaultRedirectUri,
    },
    [OAuthProviderType.Google]: {
        clientId: '445112211283-sk04feuogpcjd3dq8eshrdnr4bpm1sfk.apps.googleusercontent.com',
        scope:
            'openid email profile https://www.googleapis.com/auth/calendar https://www.google.com/m8/feeds/ https://mail.google.com https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/user.birthday.read',
        display: 'popup',
        response_type: 'code',
        oauth_proxy: '',
        force: true,
        redirect_uri: defaultRedirectUri,
    },
    [OAuthProviderType.ICloud]: {
        clientId: '',
    },
    [OAuthProviderType.Yahoo]: {
        clientId:
            'dj0yJmk9ZkVUSlFVRnpCdFNKJmQ9WVdrOWVVYzBjblJVTkdNbWNHbzlNQS0tJnM9Y29uc3VtZXJzZWNyZXQmeD02ZQ--',
        scope: 'openid',
        display: 'popup',
        response_type: 'code',
        oauth_proxy: '',
        force: true,
        redirect_uri: defaultRedirectUri,
    },
};

export async function linkAccountAndGetAccessToken(
    provider: OAuthProviderType
): Promise<ConnectedAccountResponse | ErrorResponse> {
    var loginConfig = idpLoginConfig[provider];
    let idpResponse;
    let res;
    let isAuthSuccess = false;

    try {
        idpResponse = await loginToIdp(provider, loginConfig);

        if (!idpResponse && provider == OAuthProviderType.ICloud) {
            res = {
                error: ErrorType[ErrorType.UserCancelled], // return as string
                errorDescription: 'User cancelled the login',
            } as ErrorResponse;
        } else {
            isAuthSuccess = true;
        }
    } catch (ex) {
        res = {
            error: parseError(ex?.error?.code),
            errorDescription: ex?.error?.message,
        } as ErrorResponse;
    } finally {
        // If the auth dialog is network blocked / unavailable for the user the auth dialog will
        // still appear without the content and closing the dialog wlll return UserCancelled
        if (res?.error == ErrorType[ErrorType.UserCancelled]) {
            const isHostAvailable = await isProviderHostAvailable(provider, loginConfig.clientId);

            if (!isHostAvailable) {
                res = {
                    error: ErrorType[ErrorType.ProviderUnavailable],
                    errorDescription: null,
                };
            }
        }

        logUsage(
            'addConnectedAccountAuthenticationComplete_service',
            {
                isSuccess_1: isAuthSuccess,
                errorCode: res?.error,
                errorDescription: res?.errorDescription,
            },
            {
                isCore: true,
            }
        );
    }

    if (isAuthSuccess) {
        try {
            const addConnectedAccountHandler = getAddConnectedAccountHandler(provider);
            res = await addConnectedAccountHandler(
                idpResponse.authResponse as ConnectedAccountRequest
            );

            logUsage(
                'accountAddedNewCloudCacheMailboxProvisioned',
                {
                    providerType_1: res.providerType,
                    newCloudCacheMailboxProvisioned_2: res.newCloudCacheMailboxProvisioned,
                },
                {
                    isCore: true,
                }
            );
        } catch (ex) {
            res = {
                error: ErrorType[ErrorType.Transient],
                errorDescription: ex?.error?.message,
            } as ErrorResponse;
            logUsage('addConnectedAccountHandler_catch_error');
        }
    }

    return res as ConnectedAccountResponse;
}

function parseError(errorCode: string): string {
    if (errorCode == 'access_denied') {
        return ErrorType[ErrorType.PermissionDenied];
    } else if (errorCode == 'cancelled') {
        return ErrorType[ErrorType.UserCancelled];
    } else if (errorCode == 'aad_auth') {
        return ErrorType[ErrorType.AadAuth];
    } else {
        return ErrorType[ErrorType.Transient];
    }
}
